import React, { useMemo } from "react";
import {
  alpha,
  Box,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "../../../shared/toast";
import { PasswordField } from "./PasswordField";
import { CredentialsFormValues, schemaByVendor } from "../validationSchemas";
import { vendorMeta, Vendor } from "../vendorConfig";
import { profileClient } from "@/shared/api/clients";
import { isAxiosError } from "axios";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { canManageUsers } from "../../../shared/auth/authorization";
import { useOrgId } from "../../../shared/auth/orgContext";

function resolveSecretPayload(
  vendor: Vendor,
  values: CredentialsFormValues,
  stationId: string,
): Record<string, string> {
  if (vendor === "AXON") {
    return {
      agency_uri: values.apiUrl,
      agency_id: values.agencyId ?? "",
      client_id: values.clientId ?? "",
      client_secret: values.clientSecret,
      station_id: stationId,
    };
  }
  if (vendor === "LENSLOCK") {
    return {
      base_url: values.apiUrl,
      clientId: values.clientId ?? "",
      clientSecret: values.clientSecret,
      organizationId: values.agencyId ?? "",
      station_id: stationId,
    };
  }
  return {
    client_secret: values.clientSecret,
    agency_uri: values.apiUrl,
    station_id: stationId,
  };
}

interface Props {
  vendor: Vendor;
  onBack: () => void;
  onSuccess: (vendor: Vendor) => void;
  isUpdate?: boolean;
  stationId?: string;
}

export const CredentialsForm: React.FC<Props> = ({
  vendor,
  onBack,
  onSuccess,
  isUpdate = false,
  stationId,
}) => {
  const meta = vendorMeta[vendor];
  const schema = useMemo(() => schemaByVendor(vendor), [vendor]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<CredentialsFormValues>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: {
      clientId: "",
      clientSecret: "",
      apiUrl: "",
      agencyId: "",
      tenantName: "",
    },
  });

  const toast = useToast();

  const organizationId = useOrgId();
  const effectiveStationId = stationId ?? organizationId;
  const permissions = usePermissions();
  const userCanManageUsers = canManageUsers(permissions);

  const onSubmit = async (values: CredentialsFormValues) => {
    try {
      if (!effectiveStationId) {
        throw new Error("Could not determine station for this user.");
      }

      const secret = resolveSecretPayload(vendor, values, effectiveStationId);

      const payload = { vendorType: vendor.toLowerCase(), secret };

      if (userCanManageUsers) {
        await profileClient.put(
          `/v1/stations/${effectiveStationId}/integrations/bodycam/secrets`,
          payload,
        );
      }

      onSuccess(vendor);
    } catch (error: unknown) {
      let errorMessage =
        "Could not save. Check your credentials and try again.";
      if (isAxiosError(error)) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast.error(errorMessage);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: alpha(meta.accent, 0.06),
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            component="img"
            src={meta.logo}
            alt={meta.label}
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              objectFit: "cover",
              border: `1px solid ${alpha(meta.accent, 0.2)}`,
            }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {meta.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your API credentials below
            </Typography>
          </Box>
        </Stack>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Grid container spacing={2.5}>
              {(vendor === "AXON" || vendor === "LENSLOCK") && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Client ID"
                      required
                      placeholder="3febc96e-3bcd-45cc-a7a9-5c61f4b59226"
                      fullWidth
                      autoComplete="off"
                      disabled={isSubmitting}
                      {...register("clientId")}
                      error={!!errors.clientId}
                      helperText={errors.clientId?.message ?? ""}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <PasswordField
                      label="Client Secret"
                      registration={register("clientSecret")}
                      error={errors.clientSecret?.message}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                    <TextField
                      label="Evidence URL"
                      required
                      placeholder={meta.urlPlaceholder}
                      fullWidth
                      autoComplete="off"
                      disabled={isSubmitting}
                      {...register("apiUrl")}
                      error={!!errors.apiUrl}
                      helperText={errors.apiUrl?.message ?? ""}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label={meta.agencyLabel}
                      required
                      placeholder="4fa1271c-5396-4d72-a80b-edbece9574ed"
                      fullWidth
                      autoComplete="off"
                      disabled={isSubmitting}
                      {...register("agencyId")}
                      error={!!errors.agencyId}
                      helperText={errors.agencyId?.message ?? ""}
                    />
                  </Grid>

                  {vendor === "LENSLOCK" && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Tenant Name"
                        required
                        placeholder="LensLock - Main"
                        fullWidth
                        autoComplete="off"
                        disabled={isSubmitting}
                        {...register("tenantName")}
                        error={!!errors.tenantName}
                        helperText={errors.tenantName?.message ?? ""}
                      />
                    </Grid>
                  )}
                </>
              )}

              {vendor === "WATCHGUARD" && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <PasswordField
                      label="Client Secret"
                      registration={register("clientSecret")}
                      error={errors.clientSecret?.message}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="API URL"
                      required
                      placeholder={meta.urlPlaceholder}
                      fullWidth
                      autoComplete="off"
                      disabled={isSubmitting}
                      {...register("apiUrl")}
                      error={!!errors.apiUrl}
                      helperText={errors.apiUrl?.message ?? ""}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ px: 3, py: 2, bgcolor: "action.hover" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              variant="text"
              onClick={onBack}
              startIcon={<ChevronLeft />}
              sx={{ color: "text.secondary" }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              type="submit"
              loading={isSubmitting}
              disabled={!isValid || isSubmitting}
              size="large"
              sx={{
                px: 4,
                bgcolor: meta.accent,
                "&:hover": { bgcolor: alpha(meta.accent, 0.85) },
              }}
            >
              {isUpdate ? "Save Changes" : "Connect"}
            </Button>
          </Stack>
        </Box>
      </form>
    </Paper>
  );
};
